import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/apiClient';
import './AdminPanel.css';

interface AdminCard {
  cardId: string;
  question: string;
  wonder: string;
  investigate: string;
}

interface AdminTopic {
  topicId: string;
  topicName: string;
  subject: string;
  yearGroup: number;
  keyConcepts: string[];
  cards: AdminCard[];
}

export const AdminPanel: React.FC = () => {
  const { isAdmin } = useAuth();
  const [year, setYear] = useState(5);
  const [subject, setSubject] = useState('');
  const [topics, setTopics] = useState<AdminTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingCard, setEditingCard] = useState<{ topicId: string; card: AdminCard } | null>(null);
  const [editForm, setEditForm] = useState({ question: '', wonder: '', investigate: '' });
  const [successMsg, setSuccessMsg] = useState('');
  const [flaggedCards, setFlaggedCards] = useState<any[]>([]);
  const [showFlagged, setShowFlagged] = useState(false);

  const years = [5, 6, 7, 8];
  const subjects = [
    { value: '', label: 'All subjects' },
    { value: 'maths', label: 'Maths' },
    { value: 'english', label: 'English' },
    { value: 'science', label: 'Science' },
    { value: 'history', label: 'History' },
    { value: 'geography', label: 'Geography' },
    { value: 'computing', label: 'Computing' },
    { value: 'mfl', label: 'Modern Languages' },
  ];

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (subject) params.set('subject', subject);
      const data = await apiClient.adminListCards(params.toString());
      setTopics(data.topics);
    } catch (err: any) {
      setError(err.message || 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  }, [year, subject]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const loadFlagged = async () => {
    try {
      const data = await apiClient.adminListFlagged();
      setFlaggedCards(data.flaggedCards || []);
      setShowFlagged(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load flagged cards');
    }
  };

  const handleReview = async (cardId: string, topicId: string, action: 'approve' | 'reject') => {
    try {
      await apiClient.adminApproveCard(cardId, topicId, action);
      setSuccessMsg(`Card ${action}d`);
      setFlaggedCards(prev => prev.filter(c => c.cardId !== cardId));
    } catch (err: any) {
      setError(err.message || 'Failed to review card');
    }
  };

  if (!isAdmin) return <p className="admin-denied">Admin access required.</p>;

  const startEdit = (topicId: string, card: AdminCard) => {
    setEditingCard({ topicId, card });
    setEditForm({ question: card.question, wonder: card.wonder, investigate: card.investigate });
    setSuccessMsg('');
  };

  const saveEdit = async () => {
    if (!editingCard) return;
    try {
      await apiClient.adminUpdateCard(editingCard.card.cardId, {
        topicId: editingCard.topicId,
        ...editForm,
      });
      setSuccessMsg('Card updated');
      setEditingCard(null);
      loadCards();
    } catch (err: any) {
      setError(err.message || 'Failed to update');
    }
  };

  const deleteCard = async (topicId: string, cardId: string) => {
    if (!confirm('Delete this card permanently?')) return;
    try {
      await apiClient.adminDeleteCard(cardId, topicId);
      setSuccessMsg('Card deleted');
      loadCards();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const totalCards = topics.reduce((n, t) => n + t.cards.length, 0);

  return (
    <div className="admin-panel">
      <h2>Admin: Curriculum Cards</h2>

      <div className="admin-filters">
        <label>
          Year:
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>Year {y}</option>)}
          </select>
        </label>
        <label>
          Subject:
          <select value={subject} onChange={e => setSubject(e.target.value)}>
            {subjects.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <button className="admin-btn edit" onClick={loadFlagged}>
          🚩 Review Flagged ({flaggedCards.length})
        </button>
        <span className="admin-count">{topics.length} topics · {totalCards} cards</span>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {successMsg && <p className="admin-success">{successMsg}</p>}

      {showFlagged && flaggedCards.length > 0 && (
        <div className="admin-flagged">
          <h3>🚩 Flagged Cards ({flaggedCards.length})</h3>
          {flaggedCards.map(card => (
            <div key={card.cardId} className="admin-card flagged">
              <div className="admin-card-prompts">
                <p><strong>Q:</strong> {card.question}</p>
                <p><strong>W:</strong> {card.wonder}</p>
                <p><strong>I:</strong> {card.investigate}</p>
                <p className="admin-flag-count">🚩 {card.flagCount} flag{card.flagCount !== 1 ? 's' : ''} · Status: {card.reviewStatus}</p>
              </div>
              <div className="admin-card-actions">
                <button className="admin-btn save" onClick={() => handleReview(card.cardId, card.topicId, 'approve')}>✓ Approve</button>
                <button className="admin-btn delete" onClick={() => handleReview(card.cardId, card.topicId, 'reject')}>✗ Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showFlagged && flaggedCards.length === 0 && (
        <p className="admin-success">No flagged cards to review.</p>
      )}
      {loading && <p className="admin-loading">Loading…</p>}

      {topics.map(topic => (
        <div key={topic.topicId} className="admin-topic">
          <h3>{topic.topicName} <span className="admin-topic-meta">{topic.subject} · Year {topic.yearGroup}</span></h3>
          {topic.cards.map(card => (
            <div key={card.cardId} className="admin-card">
              {editingCard?.card.cardId === card.cardId ? (
                <div className="admin-edit-form">
                  <label>Question<textarea value={editForm.question} onChange={e => setEditForm(f => ({ ...f, question: e.target.value }))} /></label>
                  <label>Wonder<textarea value={editForm.wonder} onChange={e => setEditForm(f => ({ ...f, wonder: e.target.value }))} /></label>
                  <label>Investigate<textarea value={editForm.investigate} onChange={e => setEditForm(f => ({ ...f, investigate: e.target.value }))} /></label>
                  <div className="admin-edit-actions">
                    <button className="admin-btn save" onClick={saveEdit}>Save</button>
                    <button className="admin-btn cancel" onClick={() => setEditingCard(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="admin-card-prompts">
                    <p><strong>Q:</strong> {card.question}</p>
                    <p><strong>W:</strong> {card.wonder}</p>
                    <p><strong>I:</strong> {card.investigate}</p>
                  </div>
                  <div className="admin-card-actions">
                    <button className="admin-btn edit" onClick={() => startEdit(topic.topicId, card)}>Edit</button>
                    <button className="admin-btn delete" onClick={() => deleteCard(topic.topicId, card.cardId)}>Delete</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
